import { api } from '@/api/client';
import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  toast,
} from '@kgc/ui';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface RejectApprovalDialogProps {
  approvalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRejected: () => void;
}

export function RejectApprovalDialog({
  approvalId,
  open,
  onOpenChange,
  onRejected,
}: RejectApprovalDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setReason('');
      setIsSubmitting(false);
    }
    onOpenChange(nextOpen);
  }

  async function handleReject() {
    if (!reason.trim()) return;
    setIsSubmitting(true);
    try {
      await api.post(`/approvals/${approvalId}/reject`, { reason });
      toast.success(t('integration.flow.approval.rejected'));
      handleOpenChange(false);
      onRejected();
    } catch {
      toast.error(t('integration.flow.approval.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="flow-reject-dialog">
        <DialogHeader>
          <DialogTitle>{t('integration.flow.approval.rejectTitle')}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('integration.flow.approval.reasonPlaceholder')}
          data-testid="flow-reject-reason"
          rows={3}
          aria-required="true"
        />
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => void handleReject()}
            disabled={!reason.trim() || isSubmitting}
            data-testid="flow-reject-submit-btn"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('integration.flow.approval.reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
